import { useAtom } from 'jotai';
import { cloneDeep, merge } from 'lodash-es';
import { useCallback } from 'react';
import { ICustomNumberFormatConfig, IFormatterDataModel } from '../data-models/formatter.data-model';
import { defaultCurrency } from '../services/state/AppConfigStateJ';
import { currentCurrencyCodeAtom, selectedCurrencyPrefAtom } from '../services/state/CurrencyStateJ';
import { CurrencyCode } from '../util/currency-util';
import { FMT, StandardFormatterId } from '../util/formatter-service';

/**
 * Custom hook to handle the selected currency
 */
export function useSelectedCurrency() {
  // App level preference
  const [selectedCurrencyPref, setSelectedCurrencyPref] = useAtom(selectedCurrencyPrefAtom);
  // Local state needed for now as FX is only supported in portco views.
  const [selectedCurrency, _setSelectedCurrency] = useAtom(currentCurrencyCodeAtom);

  const setSelectedCurrency = useCallback(
    (currency?: CurrencyCode, updatePref = true) => {
      const newCurrency = !currency ? defaultCurrency : currency;

      updateCurrencyFormatters(newCurrency);
      _setSelectedCurrency(newCurrency);

      if (updatePref) {
        setSelectedCurrencyPref({ currency: newCurrency });
      }
    },
    [_setSelectedCurrency, setSelectedCurrencyPref]
  );

  /**
   * Temporary function to switch to the global app currency until multi-currency support is added app-wide.
   */
  const tempSwitchToAppCurrency = useCallback(() => {
    setSelectedCurrency(selectedCurrencyPref.currency, false);
  }, [selectedCurrencyPref, setSelectedCurrency]);

  return {
    selectedCurrency,
    setSelectedCurrency,
    tempSwitchToAppCurrency,
  };
}

function updateCurrencyFormatters(newCurrency: CurrencyCode) {
  const allFormatters = FMT.get().getAllFormatterIds();
  const newFormat: Partial<IFormatterDataModel<ICustomNumberFormatConfig>> = {
    config: { currency: newCurrency },
  };

  allFormatters.forEach((formatterId) => {
    // USD formatter are legacy
    if (formatterId.startsWith('currency') || formatterId.startsWith('usd')) {
      const currentModel = FMT.get().getFormatterModel(
        formatterId as StandardFormatterId
      )! as IFormatterDataModel<ICustomNumberFormatConfig>;
      const newModel = merge(cloneDeep(currentModel), newFormat);

      FMT.get().setFormatterFromConfigForId(formatterId, newModel);
    }
  });
}
